import { ActivatedRoute } from '@angular/router';
import { Component, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { IonDatetime, IonRouterOutlet, IonSegment, IonSelect, ModalController, NavController } from '@ionic/angular';
import { Socket } from 'ngx-socket-io';
import { Subject, timer } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../auth/auth.service';
import { CreateMatchComponent } from '../../../matches/create-match/create-match.component';
import { Match } from '../../../_shared/models/match.model';
import { CollectionDefinition, CollectionValueProfile, Tie } from '../../../_shared/models/tie.model';
import { AddCollectionDefinitionComponent } from '../../add-collection-definition/add-collection-definition.component';
import { MatchService } from '../../../_core/services/match.service';
import { TieDetailsService } from '../tie-details.service';
import { LoaderService } from '../../../_core/services/loader.service';
import { BoardHelpService } from '../../../match-tracker/tracker-board/board-help.service';
import { DownloadCsvService } from '../../../_core/services/download-csv.service';
import { TieService } from '../../../_core/services/tie.service';
import { Preferences } from '@capacitor/preferences';
import { DatePipe, Location } from '@angular/common';

interface MatchInfo {
    match: Match;
    collectionValueProfile: CollectionValueProfile;
}
interface GroupedMatch {
    collectionDefinition: CollectionDefinition;
    matchInfos: MatchInfo[];
}

@Component({
  standalone: false,
    selector: 'app-tie-match-list',
    templateUrl: './tie-match-list.component.html',
    styleUrls: ['./tie-match-list.component.scss'],
})
export class TieMatchListComponent implements OnInit, OnDestroy {
    private authService = inject(AuthService);
    private location = inject(Location);
    private activatedRoute = inject(ActivatedRoute);

    @ViewChild('segment') segmentButtons: IonSegment;
    @ViewChild('startDate') startDate: IonDatetime;
    @ViewChild('selectTemplate') selectTemplate: IonSelect;

    @Input() tie: Tie;
    isTracker: boolean;
    isOwner: boolean;

    isLoading: boolean;
    isDeleting: boolean;
    numberOfViewers: number = 0;
    isAddingTemplate = false;
    isHandlingInvite = false;

    lineup = [];

    matches: Match[];
    groupedMatches: GroupedMatch[] = [];
    isLiveMatchesOnly = false;
    private skipCache = false;
    private readonly destroy$: Subject<void> = new Subject();
    isStreamView = false;
    theme: any;

    constructor(
        private title: Title,
        private tieDetailsService: TieDetailsService,
        private navCtrl: NavController,
        private matchService: MatchService,
        private socket: Socket,
        private modalController: ModalController,
        private routerOutlet: IonRouterOutlet,
        private loaderService: LoaderService,
        private bhs: BoardHelpService,
        private downloadCsvService: DownloadCsvService,
        private tieService: TieService,
        private datePipe: DatePipe
    ) { }

    async ngOnInit() {
        this.title.setTitle('iOnCourt | Track Team Match');
        this.isStreamView = this.location.path().includes('stream-view');

        if (this.isStreamView) {
            this.theme = this.activatedRoute.snapshot.params.theme;
        }
        this.isLoading = true;

        if (this.tie) {
            this.isLiveMatchesOnly = (await Preferences.get({ key: `${this.tie._id}-isLiveMatchesOnly` })).value === 'true';

            await this.getTie();
            this.getMatches();
        }

        this.subscriptions();
    }

    ngOnDestroy() {
        this.socket.emit('leave-room', this.tie._id);
        this.destroy$.next();
        this.destroy$.complete();
        this.bhs.resetTieScore();
    }

    async getTie(refresher?) {
        this.tie = await this.tieDetailsService.getTieInfo(this.tie._id, false, false, 'tie-match-list ln 100');

        if (!this.tie) {
            this.navCtrl.navigateBack('');
            return;
        }

        this.isOwner = await this.tieDetailsService.isOwner(this.tie);
        this.isTracker = this.tie.trackers.some(tracker => this.authService.authUserSignal()?._id === tracker.tracker?._id);

        if (this.isOwner || this.isTracker) {
            this.skipCache = true;
        }

        this.socket.emit('join-room', this.tie._id);

        if (refresher) refresher.detail.complete();

    }

    async getMatches(showLoader?: boolean) {
        if (showLoader) {
            await this.loaderService.present(false);
        }
        this.tieDetailsService
            .getMatches(this.tie._id, this.skipCache)
            .pipe(
                takeUntil(this.destroy$),
                finalize(
                    async () => {
                        if (showLoader) {
                            await this.loaderService.dismiss()
                        }
                    }
                )
            )
            .subscribe({
                next: (tieMatchUps: Match[]) => {
                    this.matches = tieMatchUps;
                    this.getGroupedMatches();
                    this.isLoading = false;

                    const isThemedStreamView = this.location.path().includes('stream-view');
                    if (isThemedStreamView) {
                        timer(3 * 60 * 1000)
                            .pipe(takeUntil(this.destroy$))
                            .subscribe({
                                next: async () => await this.getMatches()
                            })
                    }
                }
            });
    }

    getGroupedMatches(liveOnly = false) {
        this.groupedMatches = [];
        this.tie.tieFormat.collectionDefinitions.forEach(cd => {
            let matchInfos: MatchInfo[] = [];
            for (let i = 0; i < cd.collectionValueProfile.length; i++) {
                const collectionValueProfile = cd.collectionValueProfile[i];
                const match = this.findMatch(cd.collectionId, collectionValueProfile.collectionPosition);
                if (liveOnly && match.matchStatus === 'IN_PROGRESS' || !liveOnly) {
                    matchInfos.push({ collectionValueProfile, match });
                }
            }

            // if (this.isStreamView) {
            //     matchInfos = matchInfos.sort((a, b) => { return this.getCourtNumber(a.match) - this.getCourtNumber(b.match) })
            // }

            if (matchInfos.length) {
                this.groupedMatches.push({
                    collectionDefinition: cd,
                    matchInfos: matchInfos
                });
            }

        });
    }

    private getCourtNumber(match: Match): number {
        const extension = match.extensions?.find(e => e.name === 'court');

        if (extension) {
            const courtNumber = extension.value.courtName.match(/\d/g);

            if (courtNumber?.length) {
                return Number(extension.value.courtName.match(/\d/g)?.join(''))
            } else {
                return 0;
            }
        }

        return 100;

    }

    private findMatch(collectionId, collectionPosition) {
        const foundMatch = this.matches.find(match => match.collectionId === collectionId && match.collectionPosition === collectionPosition);
        if (foundMatch) {
            return foundMatch;
        } else {
            return null;
        }
    }

    async editMatch(match: Match, collectionDefinition, collectionPosition) {
        const modal = await this.modalController.create({
            component: CreateMatchComponent,
            presentingElement: this.routerOutlet.nativeEl,
            componentProps: { tie: this.tie, match, collectionDefinition, collectionPosition }
        });

        await modal.present();

        const { data } = await modal.onDidDismiss();

        if (data) {
            this.getMatches();
        }
    }

    async addCollection() {
        const modal = await this.modalController.create({
            component: AddCollectionDefinitionComponent,
            componentProps: { tieId: this.tie._id },
            presentingElement: this.routerOutlet.nativeEl
        });

        await modal.present();

        const { data } = await modal.onDidDismiss();

        if (data) {
            this.tie.tieFormat = data;
            this.getGroupedMatches();
        }

    }

    changeDatetime() {
        // this.startDate.open();
    }

    private subscriptions() {
        this.matchService.subscribeDelete()
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (match: Match) => {
                    this.removeMatch(match);
                }
            );

        this.matchService
            .subscribeEdit()
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                updatedMatch => {
                    this.tie.matchUps.forEach((match, i) => {
                        if (match._id === updatedMatch._id) {
                            this.tie.matchUps[i] = updatedMatch;
                        }
                    });
                }
            );
    }

    private removeMatch(removedMatch: Match) {
        this.tie.matchUps
            .forEach(
                (match: Match, i) => {
                    if (match._id === removedMatch._id) {
                        this.tie.matchUps.splice(i, 1);
                    }
                }
            );
    }

    isParticipant(matchInfo): boolean {
        return matchInfo.match.sides[0]?.players[0]?.participant || matchInfo.match.sides[1]?.players[0]?.participant;
    }

    async exportCsv() {
        const data = [];

        this.matches.forEach((match, i) => {
            data.push({
                'Type': match.matchType,
                'Position': match.collectionPosition,
                'Side 1': this.bhs.getPlayersNames(match, 1, 'full'),
                'Side 2': this.bhs.getPlayersNames(match, 2, 'full'),
                'Score': match.winningSide === 1 ? match.score?.scoreStringSide1 : match.score?.scoreStringSide2,
                'Winning side': match.winningSide,
                'Start date': this.datePipe.transform(match.startDate, 'medium'),
                'End date': this.datePipe.transform(match.endDate, 'medium'),
                'Order of finish': this.tieService.orderMatchesByOrderOfFinish(this.matches, i)
            });
        });
        data.sort((a, b) => {
            return a.Position - b.Position;
        });

        data.sort((a, b) => {
            if (a.Type < b.Type) { return -1; }
            if (a.Type > b.Type) { return 1; }
            return 0;
        });

        this.downloadCsvService.download(data, this.tie.tieId);
    }

    async changeIsLiveMatchesOnly() {
        this.isLiveMatchesOnly = !this.isLiveMatchesOnly;
        await Preferences.set({ key: `${this.tie._id}-isLiveMatchesOnly`, value: String(this.isLiveMatchesOnly) });
    }
}
